import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LiveStatusDot } from './LiveStatusDot.js';
import { useEventStream } from '../hooks/useEventStream.js';
import { UserMenu } from './UserMenu.js';
import { useCommandBar } from './CommandBar.js';
import { api } from '../lib/api.js';

const NAV_MAIN = [
  { to: '/',          label: 'Dashboard', icon: '◈' },
  { to: '/imports',   label: 'Imports',   icon: '⧉' },
  { to: '/ehrs',      label: 'EHRs',      icon: '⬢' },
  { to: '/recipes',   label: 'Recipes',   icon: '⚗' },
  { to: '/builds',    label: 'Builds',    icon: '⎔' },
  { to: '/storage',   label: 'Storage',   icon: '☁' },
  { to: '/shares',    label: 'Shares',    icon: '⇪' },
  { to: '/conflicts', label: 'Conflicts', icon: '⚠' },
] as const;

const NAV_TOOLS = [
  { to: '/tickets',   label: 'Tickets',   icon: '⬡' },
  { to: '/calendar',  label: 'Calendar',  icon: '◷' },
  { to: '/kb',        label: 'Knowledge', icon: '◎' },
] as const;

const NAV_SYSTEM = [
  { to: '/operations', label: 'Operations', icon: '⚡' },
  { to: '/settings',  label: 'Settings',  icon: '⚙' },
] as const;

function NavItem({
  to,
  label,
  icon,
  badge,
}: {
  to: string;
  label: string;
  icon: string;
  badge?: number;
}) {
  const { location } = useRouterState();
  const isActive = to === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      className="flex h-9 items-center gap-3 rounded-xl px-3 text-sm transition-all duration-150"
      style={isActive ? {
        background: 'linear-gradient(135deg, rgba(13,207,184,0.22) 0%, rgba(6,182,212,0.22) 100%)',
        boxShadow: 'inset 0 0 0 1px rgba(13,207,184,0.35)',
        color: 'var(--text-primary)',
      } : {
        color: 'var(--text-secondary)',
      }}
      onMouseEnter={(e) => {
        if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(13,207,184,0.10)';
      }}
      onMouseLeave={(e) => {
        if (!isActive) (e.currentTarget as HTMLElement).style.background = '';
      }}
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm"
        style={isActive ? {
          background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(13,207,184,0.4)',
        } : {
          background: 'var(--bg-tertiary)',
          color: 'var(--text-dimmed)',
          border: '1px solid var(--border)',
        }}
      >
        {icon}
      </span>
      <span
        className="flex-1 font-medium"
        style={{ color: isActive ? '#fff' : 'var(--text-secondary)' }}
      >
        {label}
      </span>
      {badge != null && badge > 0 && (
        <span
          className="min-w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold px-1"
          style={{ background: 'var(--accent-red)', color: '#fff' }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <div className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-text-dimmed opacity-60">
        {label}
      </div>
      {children}
    </div>
  );
}

export function Sidebar() {
  const { connection } = useEventStream({ topics: [] });
  const { open: openSearch } = useCommandBar();

  const { data: conflictData } = useQuery({
    queryKey: ['conflicts', 'count'],
    queryFn: () => api.getConflictCount(),
    refetchInterval: 30_000,
  });
  const openConflicts = conflictData?.count ?? 0;

  return (
    <aside
      className="flex w-[220px] shrink-0 flex-col"
      style={{
        background: 'var(--glass-bg)',
        backdropFilter: 'blur(var(--glass-blur))',
        WebkitBackdropFilter: 'blur(var(--glass-blur))',
        borderRight: '1px solid var(--glass-border)',
      }}
    >
      {/* Logo */}
      <div className="flex h-14 items-center gap-3 px-4" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-xl text-white text-xs font-bold shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
            boxShadow: '0 2px 12px rgba(13,207,184,0.4)',
          }}
        >
          AI
        </div>
        <div>
          <div className="text-sm font-bold leading-tight text-text-primary">Cerbo Import</div>
          <div className="text-[10px] leading-tight text-text-dimmed tracking-wider uppercase">v3 · ai platform</div>
        </div>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-border">
        <button
          type="button"
          onClick={openSearch}
          className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-xs transition-colors"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(13,207,184,0.4)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'; }}
        >
          <span className="text-sm">⌕</span>
          <span className="flex-1 text-left">Search everything…</span>
          <kbd className="text-[10px] border border-border rounded px-1 py-0.5" style={{ background: 'var(--bg-secondary)' }}>⌘K</kbd>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        <NavSection label="Workspace">
          {NAV_MAIN.map(({ to, label, icon }) => (
            <NavItem
              key={to}
              to={to}
              label={label}
              icon={icon}
              badge={label === 'Conflicts' ? openConflicts : undefined}
            />
          ))}
        </NavSection>

        <NavSection label="Tools">
          {NAV_TOOLS.map(({ to, label, icon }) => (
            <NavItem key={to} to={to} label={label} icon={icon} />
          ))}
        </NavSection>

        <NavSection label="System">
          {NAV_SYSTEM.map(({ to, label, icon }) => (
            <NavItem key={to} to={to} label={label} icon={icon} />
          ))}
        </NavSection>
      </nav>

      {/* Connection status */}
      <div className="flex h-7 items-center gap-2 px-6 text-xs text-text-secondary" style={{ borderTop: '1px solid var(--glass-border)' }}>
        <LiveStatusDot state={connection === 'disconnected' ? 'reconnecting' : connection} />
        <span className="font-mono text-[11px] opacity-70">{connection === 'disconnected' ? 'idle' : connection}</span>
      </div>

      {/* User menu */}
      <UserMenu />
    </aside>
  );
}
